import React from 'react';
import PropTypes from 'prop-types';
import { Form, Icon, Button, Modal, Tooltip } from 'antd';
import cn from 'classnames';
import { arrayMoveImmutable } from 'array-move';

import {
  fieldOptionsMap,
  unknownFieldOptions,
} from '../AdminInfoFormField/AdminInfoFormField';
import { DragHandle, SortableItem, SortableList } from './SortableComponents';

import { FieldPropType } from '../../propTypes';
import { texts } from '../../../../constants/texts';
import { StatePropType } from '../../../../../../constants/stateMachineStates';

import styles from './AdminInfoForm.module.scss';

const formItemLayout = {
  colon: false,
  labelAlign: 'left',
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
    md: { span: 6 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
    md: { span: 18 },
  },
};

export const AdminInfoForm = ({
  form,
  readOnly,
  companyId,
  isSaving,
  isSortEditing,
  fields,
  setFields,
  state,
  addDeleteFieldToQueue,
}) => {
  const { getFieldDecorator } = form;

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(fields.slice(), oldIndex, newIndex)
        .filter((el) => !!el)
        .map((fieldData, index) => {
          return {
            ...fieldData,
            orderNum: index,
          };
        });
      setFields(newData);
    }
  };

  const onDeleteClick = (field) => {
    Modal.confirm({
      title: texts.deleteConfirmation,
      content: `Поле: "${field.name}"`,
      okType: 'danger',
      okText: texts.yes,
      cancelText: texts.no,
      onOk: () => {
        addDeleteFieldToQueue(field);
      },
      onCancel: () => {},
    });
  };

  return (
    <Form
      {...formItemLayout}
      className={cn({
        [styles.form_disabled]: readOnly && !isSaving,
      })}
    >
      {isSortEditing && (
        <div style={{ padding: '16px' }}>
          <span>
            Перетащите поля так, как планируете отображать их для собственника
          </span>
        </div>
      )}
      <SortableList
        axis="y"
        lockAxis="y"
        useDragHandle
        disableAutoscroll
        helperClass={styles.formItemAction__Icon_drag}
        onSortEnd={onSortEnd}
        className={styles.sortableList}
      >
        {fields.map((field, index) => {
          const fieldOptions =
            fieldOptionsMap[field.type.name] || unknownFieldOptions;

          const isUnknown = fieldOptions === unknownFieldOptions;

          let child = null;
          let errorBlock = null;
          if (fieldOptions) {
            const decoratorOptions = fieldOptions.decoratorOptions;
            const FieldComponent = fieldOptions.render;
            getFieldDecorator(`${field.id}.orderNum`, {
              initialValue: field.orderNum,
            });
            child = getFieldDecorator(`${field.id}.value`, {
              initialValue: field.value,
              ...decoratorOptions,
            })(
              <FieldComponent
                readOnly={readOnly}
                isSaving={isSaving}
              />
            );
          }
          if (isUnknown) {
            errorBlock = (
              <div
                style={{
                  color: 'red',
                  textAlign: 'left',
                  lineHeight: 1,
                }}
              >
                Внимание, редактировние запрещено (неожиданный тип поля{' '}
                {JSON.stringify(field.type)})
              </div>
            );
          }

          return (
            <SortableItem
              key={field.id}
              id={field.id}
              index={index}
              className={cn(styles.sortableItem, {
                [styles.sortableItem_disabled]: !isSortEditing,
              })}
            >
              <div className={styles.sortableItemContent}>
                <Form.Item
                  key={field.id}
                  label={
                    <Tooltip
                      title={`${field.name} (${field.type.description})`}
                      mouseEnterDelay={1}
                    >
                      <div className={styles.formItemLabel__text}>
                        {field.name}
                      </div>
                    </Tooltip>
                  }
                >
                  {child}
                  {errorBlock}
                </Form.Item>
              </div>
              {isSortEditing && (
                <div className={styles.formItemAction}>
                  <Form.Item>
                    <DragHandle
                      className={cn(
                        styles.formItemAction__Icon,
                        styles.formItemAction__Icon_drag
                      )}
                    />
                  </Form.Item>
                </div>
              )}
              {!isSortEditing && !readOnly && (
                <div className={styles.formItemAction}>
                  <Form.Item>
                    <Button
                      type="link"
                      onClick={() => onDeleteClick(field)}
                      disabled={isSaving}
                    >
                      <Icon
                        className={cn(
                          styles.formItemAction__Icon,
                          styles.formItemAction__Icon_delete
                        )}
                        type="delete"
                      />
                    </Button>
                  </Form.Item>
                </div>
              )}
            </SortableItem>
          );
        })}
      </SortableList>
    </Form>
  );
};

AdminInfoForm.propTypes = {
  form: PropTypes.object.isRequired,
  readOnly: PropTypes.bool.isRequired,
  companyId: PropTypes.number.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isSortEditing: PropTypes.bool.isRequired,

  fields: PropTypes.arrayOf(FieldPropType).isRequired,
  state: StatePropType.isRequired,
  addDeleteFieldToQueue: PropTypes.func.isRequired,
};
